<template>
  <Teleport to="body">
    <Modal ref="modal" v-model="show">
      <ModalContent size="medium">
        <template #header>
          <Heading level="h2">{{ t('station_management.title') }}</Heading>
        </template>

        <div class="tw-space-y-4">
          <FormField
            type="select"
            v-model="columnSelection"
            :label="t('station_management.number_of_columns')"
            class="tw-hidden tw-w-full lg:tw-block"
          >
            <option value="auto">Auto</option>
            <option v-for="n in 10" :key="n" :value="String(n)">{{ n }}</option>
          </FormField>

          <div class="tw-space-y-2">
            <Checkbox
              :model-value="allStationsSelected"
              @update:model-value="(value) => toggleSelectAllStations(value)"
              :indeterminate="indeterminateSelectAll"
              :right-label="t('station_management.select_all')"
            />

            <VueDraggable
              v-if="selectedHosts.length > 0"
              v-model="selectedHosts"
              tag="div"
              class="tw-space-y-1"
              :animation="150"
            >
              <StationSelectCheckbox
                v-for="host in selectedHosts"
                :key="host"
                drag-handle
                :station="stationsByHost[host]"
                :model-value="true"
                @update:model-value="(val) => toggleSelectedStation(host, val)"
              />
            </VueDraggable>

            <div v-if="selectedHosts.length > 0 && inactiveStations.length > 0" class="tw-h-px tw-bg-white/12" />

            <div v-if="inactiveStations.length > 0" class="tw-space-y-1">
              <StationSelectCheckbox
                v-for="station in inactiveStations"
                :key="station.key"
                :station="station"
                :model-value="false"
                @update:model-value="(val) => toggleSelectedStation(station.host, val)"
              />
            </div>
          </div>

          <div class="tw-pl-6">
            <Button v-if="!showAddField" color="semiTransparentWhite" @click="enableAddField">
              {{ t('station_management.add_station') }}
            </Button>

            <div v-else class="tw-flex tw-items-start tw-gap-2">
              <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                <FormField
                  v-model="newHost"
                  type="text"
                  :label="t('station_management.station_ip')"
                  :wrapper-attrs="{ class: newHostError ? 'error' : '' }"
                  :message="newHostError || ''"
                  :message-attrs="{ class: newHostError ? 'tw-text-state-err' : '' }"
                  placeholder="10.210.21.2"
                />
              </div>

              <Button color="semiTransparentWhite" @click="addNewStation">
                {{ t('station_management.add_station') }}
              </Button>
            </div>
          </div>
        </div>

        <template #footer>
          <Button color="transparent" outlined @click="cancel">{{ t('common.cancel') }}</Button>
          <Button color="primary" @click="save">{{ t('common.save') }}</Button>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, toValue, watch } from 'vue';
import { VueDraggable } from 'vue-draggable-plus';
import { useI18n } from 'vue-i18n';

import { Button, Checkbox, FormField, Heading, Modal, ModalContent } from '@slideslive/fuse-kit/vue';

import useSettingsStore from '@/stores/settings';
import useStationsStore from '@/stores/stations';
import useVpnStore from '@/stores/vpn';

import StationSelectCheckbox from './StationSelectCheckbox.vue';

const { t } = useI18n();
const show = defineModel();

const { refresh: refreshVpn } = useVpnStore();

const stationsStore = useStationsStore();
const { stationsList, selectedHosts: selectedHostsStore } = storeToRefs(stationsStore);

const { selectStations, addStation, refreshAll: refreshAllStations } = stationsStore;

const settingsStore = useSettingsStore();
const { gridColumns } = storeToRefs(settingsStore);

const selectedHosts = ref([]);
const columnSelection = ref('auto');

const showAddField = ref(false);
const newHost = ref('');
const newHostError = ref('');

const stationsCount = computed(() => stationsList.value.length);
const selectedStationsCount = computed(() => selectedHosts.value.length);

const anyStationSelected = computed(() => selectedStationsCount.value > 0);
const allStationsSelected = computed(() => selectedStationsCount.value === stationsCount.value);
const indeterminateSelectAll = computed(() => anyStationSelected.value && !allStationsSelected.value);

const stationsByHost = computed(() => Object.fromEntries(stationsList.value.map((s) => [s.host, s])));
const inactiveStations = computed(() => stationsList.value.filter((s) => !selectedHosts.value.includes(s.host)));

watch(show, (visible) => {
  if (!visible) return;

  refreshVpn();
  refreshAllStations();

  selectedHosts.value = [...toValue(selectedHostsStore)];
  columnSelection.value = String(gridColumns.value ?? 'auto');
  showAddField.value = false;
  newHost.value = '';
  newHostError.value = '';
});

function toggleSelectedStation(host, selected) {
  const index = selectedHosts.value.indexOf(host);
  if (selected) {
    if (index < 0) selectedHosts.value.push(host);
  } else if (index >= 0) {
    selectedHosts.value.splice(index, 1);
  }
}

function toggleSelectAllStations(selected) {
  if (selected) {
    const currentlySelected = new Set(selectedHosts.value);
    const toSelect = stationsList.value.filter((s) => !currentlySelected.has(s.host)).map((s) => s.host);
    selectedHosts.value = [...selectedHosts.value, ...toSelect];
  } else {
    selectedHosts.value = [];
  }
}

function ipValid(ip) {
  return /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/.test(ip);
}

function normalizeHostInput(raw) {
  const value = String(raw ?? '').trim();
  return ipValid(value) ? value : null;
}

async function addNewStation() {
  newHostError.value = '';

  const normalized = normalizeHostInput(newHost.value);

  if (!normalized) {
    newHostError.value = t('station_management.ip_invalid_example');
    return;
  }

  const alreadyExists = stationsList.value.some((station) => station.host === normalized);
  if (alreadyExists) {
    newHostError.value = t('station_management.ip_already_exists');
    return;
  }

  await addStation(normalized);

  if (!selectedHosts.value.includes(normalized)) {
    selectedHosts.value.push(normalized);
  }

  newHost.value = '';
  showAddField.value = false;
}

function enableAddField() {
  showAddField.value = true;
}

function save() {
  selectStations(selectedHosts.value);
  gridColumns.value = columnSelection.value;
  show.value = false;
}

function cancel() {
  show.value = false;
}
</script>
